import React, { useEffect, useState, useCallback } from 'react'
import { Box, Typography, Chip, Pagination, IconButton, Drawer, Tooltip, CircularProgress } from '@mui/material'
import ClearIcon from '@mui/icons-material/Close'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  Search,
  Filter,
  X,
  FileText,
  TrendingUp,
} from 'lucide-react'
import {
  searchLegalNotices,
  fetchEntreprisesStats,
  setEntreprisesFilters,
  clearEntreprisesFilters,
  setEntreprisesPage,
  resetEntreprises,
} from '../../redux/actions/entreprisesAction'
import { useSearchTokens } from '../SearchScreen/searchDesignTokens'
import EntreprisesSearchBar from './components/EntreprisesSearchBar'
import EntreprisesFilterPanel from './components/EntreprisesFilterPanel'
import AnnonceCard from './components/AnnonceCard'
import { SearchResultsSkeleton, StatsHeroSkeleton } from './components/EntreprisesSkeleton'

const QUICK_FILTER_TYPES = [
  'constitution',
  'dissolution',
  'modification_capital',
  'cession_parts',
  'changement_gerant',
  'transfert_siege',
]

const EntreprisesScreen = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const dk = useSearchTokens()

  const {
    items,
    query,
    hadResults,
    stats,
    facets,
    filters,
    pagination,
    loading,
  } = useSelector((state) => state.entreprises)

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const hasResults = hadResults || items.length > 0
  const activeFilterCount = Object.keys(filters).reduce((acc, key) => {
    const val = filters[key]
    if (Array.isArray(val)) return acc + val.length
    if (val !== null && val !== undefined && val !== '') return acc + 1
    return acc
  }, 0)

  useEffect(() => {
    if (!stats && !loading.stats) {
      dispatch(fetchEntreprisesStats())
    }
    return () => {
      // Don't reset on unmount to preserve state when navigating back
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuickFilter = useCallback((typeAnnonce) => {
    const currentTypes = filters.type_annonce || []
    const isActive = currentTypes.includes(typeAnnonce)
    const newTypes = isActive
      ? currentTypes.filter((t) => t !== typeAnnonce)
      : [...currentTypes, typeAnnonce]

    const newFilters = { ...filters }
    if (newTypes.length > 0) {
      newFilters.type_annonce = newTypes
    } else {
      delete newFilters.type_annonce
    }
    dispatch(setEntreprisesFilters(newFilters))
    dispatch(searchLegalNotices({ filters: newFilters, page: 1 }))
  }, [filters, dispatch])

  const handlePageChange = useCallback((event, newPage) => {
    dispatch(setEntreprisesPage(newPage))
    dispatch(searchLegalNotices({ page: newPage }))
    const scrollEl = document.querySelector('[data-entreprises-scroll]')
    if (scrollEl) scrollEl.scrollTo({ top: 0, behavior: 'smooth' })
  }, [dispatch])

  const handleClearAllFilters = useCallback(() => {
    dispatch(clearEntreprisesFilters())
    dispatch(searchLegalNotices({ filters: {}, page: 1 }))
  }, [dispatch])

  const handleClearSearch = useCallback(() => {
    dispatch(resetEntreprises())
  }, [dispatch])

  // ── Landing View ────────────────────────────────────────────────────────
  if (!hasResults) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          backgroundColor: dk.bg,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, md: 6 },
            py: 4,
            backgroundColor: dk.surface,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '15%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80%',
              height: '45%',
              background: `radial-gradient(ellipse 70% 50% at 50% 30%, ${dk.goldGlow} 0%, transparent 70%)`,
              pointerEvents: 'none',
            },
          }}
        >
          {/* Stats Hero */}
          {loading.stats ? (
            <StatsHeroSkeleton dk={dk} />
          ) : stats ? (
            <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
                <Building2 size={28} color={dk.gold} strokeWidth={1.5} />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 300,
                    color: dk.text1,
                    letterSpacing: '-0.02em',
                    fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
                    fontSize: { xs: '1.6rem', md: '2.2rem' },
                  }}
                >
                  {t('entreprises_title')}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: dk.text2, maxWidth: 520, mx: 'auto', fontSize: '0.92rem', lineHeight: 1.6, mb: 3 }}
              >
                {t('entreprises_subtitle')}
              </Typography>

              {/* Stat counters */}
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 3, md: 5 },
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <StatCard
                  icon={<FileText size={18} />}
                  value={stats.total_announcements?.toLocaleString() || '0'}
                  label={t('entreprises_total_announcements')}
                  dk={dk}
                />
                <StatCard
                  icon={<TrendingUp size={18} />}
                  value={stats.total_companies?.toLocaleString() || '0'}
                  label={t('entreprises_total_companies')}
                  dk={dk}
                />
              </Box>
            </Box>
          ) : null}

          {/* Search bar */}
          <Box sx={{ width: '100%', maxWidth: 680, position: 'relative', zIndex: 1, mb: 3 }}>
            <EntreprisesSearchBar />
          </Box>

          {/* Quick filter chips */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: 700,
              position: 'relative',
              zIndex: 1,
            }}
          >
            {QUICK_FILTER_TYPES.map((type) => {
              const isActive = (filters.type_annonce || []).includes(type)
              return (
                <Chip
                  key={type}
                  label={t(`type_${type}`)}
                  clickable
                  onClick={() => handleQuickFilter(type)}
                  sx={{
                    backgroundColor: isActive ? dk.chipBgActive : dk.chipBg,
                    color: isActive ? dk.chipTextActive : dk.chipText,
                    border: `1px solid ${isActive ? dk.gold : dk.border}`,
                    borderRadius: '20px',
                    fontSize: '0.76rem',
                    fontWeight: 500,
                    height: 30,
                    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                    '& .MuiChip-label': { px: 1.5 },
                    '&:hover': {
                      backgroundColor: isActive ? dk.chipBgActive : dk.goldSub,
                      borderColor: dk.gold,
                      transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'translateY(0)' },
                  }}
                />
              )
            })}
          </Box>

          {/* Show all link */}
          <Typography
            onClick={() => dispatch(searchLegalNotices({ q: '', filters: {}, page: 1 }))}
            sx={{
              mt: 2,
              fontSize: '0.78rem',
              color: dk.text3,
              cursor: loading.search ? 'default' : 'pointer',
              position: 'relative',
              zIndex: 1,
              transition: 'color 0.15s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: loading.search ? dk.text3 : dk.gold },
            }}
          >
            {t('show_all_announcements', 'Voir toutes les annonces')}{' '}
            {loading.search ? (
              <CircularProgress size={12} sx={{ color: dk.gold }} />
            ) : (
              '→'
            )}
          </Typography>
        </Box>
      </Box>
    )
  }

  // ── Results View ────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: dk.bg,
        overflow: 'hidden',
      }}
    >
      {/* Sticky Search Header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          backgroundColor: dk.surface,
          borderBottom: `1px solid ${dk.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flexGrow: 1, maxWidth: 700 }}>
          <EntreprisesSearchBar compact />
        </Box>

        {/* Clear search */}
        <Tooltip title={t('clear_search_results', 'Effacer la recherche')} arrow>
          <IconButton
            onClick={handleClearSearch}
            size="small"
            sx={{
              color: dk.text2,
              flexShrink: 0,
              width: 26,
              height: 26,
              '&:hover': {
                backgroundColor: dk.goldSub,
                color: dk.gold,
              },
            }}
          >
            <ClearIcon sx={{ fontSize: '0.95rem' }} />
          </IconButton>
        </Tooltip>

        {/* Mobile filter toggle */}
        <IconButton
          onClick={() => setFilterDrawerOpen(true)}
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: activeFilterCount > 0 ? dk.gold : dk.text2,
            backgroundColor: activeFilterCount > 0 ? dk.goldSub : dk.chipBg,
            border: `1px solid ${activeFilterCount > 0 ? dk.goldBdr : dk.border}`,
            borderRadius: '8px',
            p: 0.8,
            '&:hover': { backgroundColor: dk.goldHover },
          }}
        >
          <Filter size={18} />
          {activeFilterCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: dk.gold,
                color: '#000',
                fontSize: '0.6rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </IconButton>
      </Box>

      {/* Main content area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Desktop Filter Sidebar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <EntreprisesFilterPanel />
        </Box>

        {/* Mobile Filter Drawer */}
        <Drawer
          anchor="left"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: 300,
              backgroundColor: dk.surface,
              borderRight: `1px solid ${dk.border}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton
              size="small"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{ color: dk.text2 }}
            >
              <X size={18} />
            </IconButton>
          </Box>
          <EntreprisesFilterPanel mobile onClose={() => setFilterDrawerOpen(false)} />
        </Drawer>

        {/* Results Area */}
        <Box
          data-entreprises-scroll
          data-tour="entreprises-results"
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            px: { xs: 2, md: 3 },
            py: 2,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: dk.scrollThumb,
              borderRadius: 2,
            },
          }}
        >
          {/* Results header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Search size={14} color={dk.text3} />
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: dk.text2,
                }}
              >
                {pagination.total > 0
                  ? `${pagination.total.toLocaleString()} ${t('results_count')}`
                  : t('no_entreprises_results')}
              </Typography>
            </Box>

            {activeFilterCount > 0 && (
              <Chip
                label={`${t('clear_all_filters')} (${activeFilterCount})`}
                size="small"
                onDelete={handleClearAllFilters}
                deleteIcon={<X size={12} />}
                sx={{
                  backgroundColor: dk.goldSub,
                  color: dk.gold,
                  border: `1px solid ${dk.goldBdr}`,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  height: 24,
                  '& .MuiChip-deleteIcon': {
                    color: dk.gold,
                    '&:hover': { color: dk.text1 },
                  },
                }}
              />
            )}
          </Box>

          {/* Results list */}
          {loading.search ? (
            <SearchResultsSkeleton dk={dk} />
          ) : items.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {items.map((item, index) => (
                  <AnnonceCard key={item.annonce_id || index} annonce={item} />
                ))}
              </Box>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 3,
                    p: 1.5,
                    backgroundColor: dk.elevated,
                    borderRadius: '8px',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.8rem', color: dk.text3, fontWeight: 500 }}
                  >
                    {`${(pagination.page - 1) * pagination.page_size + 1}-${Math.min(
                      pagination.page * pagination.page_size,
                      pagination.total
                    )} / ${pagination.total.toLocaleString()}`}
                  </Typography>
                  <Pagination
                    count={pagination.total_pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    size="small"
                    showFirstButton
                    showLastButton
                    sx={{
                      '.MuiPaginationItem-root': {
                        color: dk.text2,
                        fontWeight: 500,
                        borderRadius: '4px',
                        minWidth: '32px',
                        height: '32px',
                        '&:hover': { backgroundColor: dk.hover },
                      },
                      '.MuiPaginationItem-root.Mui-selected': {
                        backgroundColor: dk.gold,
                        color: '#000',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: dk.gold, opacity: 0.9 },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 10,
              }}
            >
              <Search size={40} color={dk.disabled} strokeWidth={1} />
              <Typography
                sx={{ color: dk.text3, fontSize: '0.95rem', mt: 2, textAlign: 'center' }}
              >
                {t('no_entreprises_results')}
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  label={t('clear_all_filters')}
                  clickable
                  onClick={handleClearAllFilters}
                  sx={{
                    mt: 2,
                    backgroundColor: dk.goldSub,
                    color: dk.gold,
                    border: `1px solid ${dk.goldBdr}`,
                    '&:hover': { backgroundColor: dk.goldHover },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// ── Stat Card (landing) ─────────────────────────────────────────────────

const StatCard = ({ icon, value, label, dk }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      px: 3,
      py: 2,
      borderRadius: '12px',
      backgroundColor: dk.elevated,
      border: `1px solid ${dk.border}`,
      minWidth: 140,
    }}
  >
    <Box sx={{ color: dk.gold, mb: 0.5 }}>{icon}</Box>
    <Typography
      sx={{
        fontSize: '1.6rem',
        fontWeight: 700,
        color: dk.text1,
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.72rem',
        fontWeight: 500,
        color: dk.text3,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {label}
    </Typography>
  </Box>
)

export default EntreprisesScreen
